/**
 * @copyright 2022 Nuance Communications Inc.
 * All Rights Reserved.
 */

import {
    ControlType,
    FieldType,
    ISettingGroupDefinition,
    Levels,
    SettingGroupDefinition,
    StringFieldDefinition,
    StringResource,
    TextFormGroup,
    UIDefinitions
} from "@nuance/hierarchical-settings-ui-domain";
import {
    IFieldDefinitionPanelTextData,
    IFieldDefinitionPanelTextProps,
    IFieldDefinitionsPanelData,
    IFieldDefinitionsPanelProps
} from "../Panels";
import { getLogger, ILogger } from "@nuance/hux-diagnostics";
import { IDomainDataMapper } from "./IDomainDataMapper";

const logger: ILogger = getLogger();
const defaultRegexErrorMessage = "Invalid value";

export class DomainDataMapperText implements IDomainDataMapper {
    /**
     * Field type expected by this mapper
     */
    readonly RequiredFieldType = FieldType.String;

    /**
     * Control type expected by this mapper
     */
    readonly RequiredControlType = ControlType.TextField;

    /**
     * Setting group definition object
     */
    readonly definition: ISettingGroupDefinition;

    /**
     * Constructor
     * @param definition Setting group definition object
     */
    constructor(definition: ISettingGroupDefinition) {
        this.definition = definition;
    }

    /**
     * Builds the text panel properties for a new text control
     * @returns Text panel properties
     */
    BuildPanelPropsForNewItem = (): IFieldDefinitionsPanelProps => {
        const result: IFieldDefinitionPanelTextProps = {
            common: {
                panelTitle: "Add Text Field",
                originalSettingName: "",
                isNew: true,
                isOpen: true
            },
            data: {
                definitionType: this.RequiredControlType,
                settingName: "",
                title: "",
                tooltip: "",
                helper: "",
                helpTextValid: true,
                defaultValue: "",
                minLength: 0,
                maxLength: 50,
                regexValue: "",
                regexErrorMessage: defaultRegexErrorMessage,
                includeRegex: false,
                accessLevels: Levels.Classic,
                auditChanges: false,
                visibleRule: "",
                enableRule: "",
                isRequired: true
            }
        };

        return result;
    };

    /**
     * Builds the text panel properties for an existing control.
     * @param ctrlGuid Control GUID of the text field
     * @returns Text Panel properties
     */
    BuildPanelPropsForExistingItem = (ctrlGuid: string): IFieldDefinitionsPanelProps => {
        const sg = new SettingGroupDefinition(this.definition);
        const [tfg, sfd] = this.findControlAndFieldDefinition(sg, ctrlGuid);
        const settingName = tfg.SettingName;

        const sr = sg.UI.StringResources;

        const result: IFieldDefinitionPanelTextProps = {
            common: {
                panelTitle: "Edit Text Field",
                originalSettingName: settingName,
                isNew: false,
                isOpen: true
            },
            data: {
                ctrlGuid,
                definitionType: this.RequiredControlType,
                settingName: settingName,
                title:
                    sr.get(StringResource.CreateMapKey(tfg.LabelTextStringId, "en-US"))?.Text ?? "",
                tooltip:
                    sr.get(StringResource.CreateMapKey(tfg.TooltipStringId, "en-US"))?.Text ?? "",
                helper:
                    sr.get(StringResource.CreateMapKey(tfg.HelperTextStringId, "en-US"))?.Text ??
                    "",
                regexErrorMessage:
                    sr.get(StringResource.CreateMapKey(tfg.RegexErrorMessageStringId, "en-US"))
                        ?.Text ?? "",
                helpTextValid: true,
                defaultValue: sfd.DefaultValue,
                minLength: sfd.MinLength ?? 0,
                maxLength: sfd.MaxLength,
                regexValue: sfd.RegexTemplate,
                includeRegex: sfd.RegexTemplate.length > 0,
                accessLevels: sfd.AccessLevel,
                auditChanges: sfd.AuditChanges,
                visibleRule: tfg.VisibleRuleName,
                enableRule: tfg.EnableRuleName,
                isRequired: sfd.IsRequired
            }
        };

        return result;
    };

    /**
     * Saves the panel control data to the settings definition.
     * @param data Panel data
     * @returns A new setting definition object with changes made
     */
    SaveDataToDefinition = (data: IFieldDefinitionsPanelData): ISettingGroupDefinition => {
        if (data.definitionType !== this.RequiredControlType) {
            logger.logError("Invalid control type", { type: ControlType[data.definitionType] });
            throw new Error("Invalid definition type.");
        }

        const td = data as IFieldDefinitionPanelTextData;
        const sg = new SettingGroupDefinition(this.definition);

        if (data.ctrlGuid === undefined) {
            this.addNewTextField(td, sg);
        } else {
            this.updateExistingTextField(td, sg);
        }

        return sg.toISettingGroupDefinition();
    };

    /**
     * Adds a new text field to the setting definition
     * @param td Panel text data
     * @param sg Setting group definition
     */
    private addNewTextField(td: IFieldDefinitionPanelTextData, sg: SettingGroupDefinition): void {
        const sfd = new StringFieldDefinition(td.settingName, td.defaultValue);
        sfd.AccessLevel = td.accessLevels;
        sfd.AuditChanges = td.auditChanges;
        sfd.MaxLength = td.maxLength;
        sfd.MinLength = td.minLength;
        sfd.RegexTemplate = td.includeRegex ? td.regexValue : "";
        sfd.IsRequired = td.isRequired;
        sg.AddFieldDefinition(sfd);

        const tfg = sg.UI.AddTextFormGroup(td.settingName);
        tfg.EnableRuleName = td.enableRule;
        tfg.VisibleRuleName = td.visibleRule;

        this.updateStringResources(
            sg.UI,
            tfg,
            td.title,
            td.tooltip,
            td.helper,
            td.includeRegex ? td.regexErrorMessage : defaultRegexErrorMessage
        );
    }

    /**
     *
     * @param td Panel text data
     * @param sg String group definition
     */
    private updateExistingTextField(
        td: IFieldDefinitionPanelTextData,
        sg: SettingGroupDefinition
    ): void {
        if (td.ctrlGuid === undefined) {
            logger.logError("Control guid is undefined.");
            throw new Error("Control guid is undefined.");
        }

        const [tfg, sfd] = this.findControlAndFieldDefinition(sg, td.ctrlGuid);
        sfd.DefaultValue = td.defaultValue;
        sfd.AccessLevel = td.accessLevels;
        sfd.AuditChanges = td.auditChanges;
        sfd.MaxLength = td.maxLength;
        sfd.MinLength = td.minLength;
        sfd.RegexTemplate = td.includeRegex ? td.regexValue : "";
        sfd.IsRequired = td.isRequired;
        tfg.EnableRuleName = td.enableRule;
        tfg.VisibleRuleName = td.visibleRule;
        this.updateStringResources(
            sg.UI,
            tfg,
            td.title,
            td.tooltip,
            td.helper,
            td.includeRegex ? td.regexErrorMessage : defaultRegexErrorMessage
        );
    }

    /**
     * Updates the string resources in the definition.
     * @param sg Setting group definition class
     * @param tfg Text form group
     * @param label Label text
     * @param tooltip Tooltip text
     * @param helper Helper text
     */
    private updateStringResources(
        ui: UIDefinitions,
        tfg: TextFormGroup,
        label: string,
        tooltip: string,
        helper: string,
        regexErrorMessage: string
    ) {
        if (!ui.UpdateStringResourceText(tfg.LabelTextStringId, label)) {
            logger.logError("Error finding label string resource", {
                stringId: tfg.LabelTextStringId
            });
            throw new Error("Error getting label string resource");
        }

        if (!ui.UpdateStringResourceText(tfg.TooltipStringId, tooltip)) {
            logger.logError("Error finding tooltip string resource", {
                stringId: tfg.TooltipStringId
            });
            throw new Error("Error getting tooltip string resource");
        }

        if (!ui.UpdateStringResourceText(tfg.HelperTextStringId, helper)) {
            logger.logError("Error finding helper text string resource", {
                stringId: tfg.HelperTextStringId
            });
            throw new Error("Error getting helper string resource");
        }

        if (!ui.UpdateStringResourceText(tfg.RegexErrorMessageStringId, regexErrorMessage)) {
            logger.logError("Error finding regex error message string resource", {
                stringId: tfg.RegexErrorMessageStringId
            });
            throw new Error("Error getting regex error message resource");
        }
    }

    /**
     * Find the control and field definition for a
     * control GUID
     * @param sg Setting group definition
     * @param ctrlGuid Control GUID
     * @returns TextFormGroup and StringFieldDefinition
     * associated with the control
     */
    private findControlAndFieldDefinition = (
        sg: SettingGroupDefinition,
        ctrlGuid: string
    ): [TextFormGroup, StringFieldDefinition] => {
        const ctrlIndex = sg.UI.Controls.findIndex(i => i.CtrlGuid == ctrlGuid);
        if (ctrlIndex === -1) {
            logger.logError("Unable to find control", { ctrlGuid });
            throw new Error("Unable to find control.");
        }

        const uiCtrl = sg.UI.Controls[ctrlIndex];
        if (uiCtrl.ControlDiscriminator !== this.RequiredControlType) {
            logger.logError("Control type is not text", {
                type: ControlType[uiCtrl.ControlDiscriminator]
            });
            throw new Error("Invalid control type.  Expecting TextField control.");
        }

        const tfg = uiCtrl as TextFormGroup;
        const fd = sg.Fields.get(tfg.SettingName);
        if (fd === undefined || fd.FieldDiscriminator !== this.RequiredFieldType) {
            logger.logError("Field type is not string", {
                type: fd?.FieldDiscriminator.toString()
            });
            throw new Error("Invalid field type.");
        }

        return [tfg, fd as StringFieldDefinition];
    };
}
